import PlayArrowOutlined from "@mui/icons-material/PlayArrowOutlined";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { getPublicConfig } from "../../../api/config/getPublicConfig";
import { createExpenseClaim } from "../../../api/dossier/createExpenseClaim";
import { extractReceiptData } from "../../../api/dossier/extractReceiptData";
import { getMyContact } from "../../../api/user/getMyContact";
import APDetailList, {
  APDetailListItem,
} from "../../../components/APDetailList";
import APButton from "../../../components/buttons/APButton";
import APContactHelpButton from "../../../components/buttons/APContactHelpButton";
import BackButton from "../../../components/buttons/BackButton";
import DossierSelector from "../../../components/dossier/DossierSelector";
import APAutoComplete, {
  AutoCompleteOption,
} from "../../../components/form/APAutoComplete";
import APDateField from "../../../components/form/APDateField";
import APFileUpload from "../../../components/form/APFileUpload";
import APForm from "../../../components/form/APForm";
import APTextField from "../../../components/form/APTextField";
import PageContainer from "../../../components/PageContainer";
import RestrictedAppScope from "../../../components/RestrictedAppScope";
import {
  getEVHomeChargingRate,
  isOCRExpenseClaimReceiptEnabled,
} from "../../../helpers/configuration";
import { toDollars, toFormattedNumber } from "../../../helpers/currencies";
import {
  dateFromString,
  endOfDay,
  now,
  yearsAgo,
} from "../../../helpers/dates";
import {
  calculateHomeChargingIncGst,
  filterExpenseClaimChargingTypeOptions,
  filterExpenseClaimTypeOptions,
} from "../../../helpers/expenseClaims";
import { useDossierActivationCheck } from "../../../hooks/useDossierActivationCheck";
import { useFaqs } from "../../../hooks/useFaqs";
import { useAuth } from "../../../providers/AuthProvider";
import { Dossier } from "../../../types/Dossier";
import {
  ExpenseClaimChargingType,
  ExpenseClaimChargingTypeOptions,
  ExpenseClaimType,
  SubmittedExpenseClaimTypeOptions,
} from "../../../types/ExpenseClaim";
import DossierBalances from "../components/DossierBalances";

type ClaimInputs = {
  dossier: AutoCompleteOption | null;
  claimType: AutoCompleteOption | null;
  taxInvoice: File | null;
  transactionDate: string | null;
  description: string;
  vendor: string;
  gst: string;
  incGst: string;
  chargingType: AutoCompleteOption | null;
  lastOdometer: string;
  currentOdometer: string;
  percentageChargeAtHome: string;
  chargingReport: File | null;
};

const CreateDossierExpenseClaim = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getScopedPageUrl } = useAuth();
  const { dossierId } = useParams<{
    dossierId: string;
  }>();
  const {
    control,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
    getValues,
    watch,
  } = useForm<ClaimInputs>({ mode: "onTouched" });
  const [isConfirming, setIsConfirming] = useState(false);
  const [minCurrentOdometer, setMinCurrentOdometer] = useState(0);
  const {
    navigationCheckComplete,
    isLoading: isLoadingDossierActivationCheck,
    isSuccess: isSuccessDossierActivationCheck,
    activeDossiers,
  } = useDossierActivationCheck({
    dossierId,
    activeDossiersOnly: true,
  });
  const {
    data: contact,
    isLoading: isLoadingContact,
    isSuccess: isSuccessContact,
    isFetching: isFetchingContact,
  } = getMyContact();

  const selectedDossier = activeDossiers.find(
    (dossier) => dossier.id === dossierId
  );

  const {
    faqProps,
    isLoading: isLoadingFaqs,
    isSuccess: isSuccessFaqs,
  } = useFaqs({
    pageId: "expense-claim-create",
    contextIds: {
      opportunityId: selectedDossier?.opportunity?.id,
    },
  });

  const configMap = getPublicConfig();

  const { mutateAsync, isLoading: isLoadingMutate } = createExpenseClaim(
    dossierId ?? "",
    {
      mutationOptions: {
        successNavPath: `/dossiers/${dossierId}/expense-claims?pollClaims=true`,
        successReplaceHistory: true,
      },
    }
  );
  const {
    mutateAsync: extractReceiptDataAsync,
    isLoading: isLoadingExtractReceiptData,
    isSuccess: isSuccessExtractReceiptData,
    data: receiptData,
  } = extractReceiptData(dossierId ?? "");

  const watchDossier = watch("dossier");
  const claimType = watch("claimType");
  const chargingType = watch("chargingType");
  const taxInvoice = watch("taxInvoice");
  const lastOdometer = watch("lastOdometer");
  const currentOdometer = watch("currentOdometer");
  const percentageChargeAtHome = watch("percentageChargeAtHome");
  const incGst = watch("incGst");
  const displayGst = claimType?.id === "insurance" || claimType?.id === "rego";

  const onSubmit: SubmitHandler<ClaimInputs> = async (data) => {
    if (!isConfirming) {
      setIsConfirming(true);
      window.scrollTo(0, 0);
    } else if (data.claimType && data.transactionDate) {
      const percentageChargeAtHome =
        filteredChargingTypeOptions.length > 1
          ? data.percentageChargeAtHome
          : "100";
      const description =
        data.chargingType?.id === "home-charging"
          ? `${percentageChargeAtHome}% Home charging ${toFormattedNumber(
              parseInt(data.lastOdometer),
              0
            )}km to ${toFormattedNumber(parseInt(data.currentOdometer), 0)}km`
          : data.description;

      const claimTypeId =
        data.claimType.id === "charging"
          ? "fuel"
          : (data.claimType.id as ExpenseClaimType);
      const ocrRequestId = receiptData?.ocrRequestId;

      mutateAsync({
        claimType: claimTypeId,
        ocrRequestId,
        transactionDate: data.transactionDate,
        description: description,
        incGst: parseFloat(data.incGst),
        exGst: data.gst ? parseFloat(data.incGst) - parseFloat(data.gst) : null,
        vendor: data.vendor,
        taxInvoice: data.taxInvoice,
        chargingType: data.chargingType
          ? (data.chargingType.id as ExpenseClaimChargingType)
          : null,
        currentOdometer: data.currentOdometer
          ? parseInt(data.currentOdometer)
          : null,
        chargingReport: data.chargingReport,
      });
    }
  };

  const onConfirmBack = () => {
    setIsConfirming(false);
    window.scrollTo(0, 0);
  };

  const toDossierOption = (dossier: Dossier) => ({
    label: `${dossier.vehicleModelyear} ${dossier.vehicleMakeName} ${dossier.vehicleModelName}`,
    id: dossier.id,
  });
  const dossierOptions = activeDossiers.map(toDossierOption);

  const clearFieldsExcluding = (
    exclude: (
      | "claimType"
      | "chargingType"
      | "lastOdometer"
      | "taxInvoice"
    )[] = []
  ) => {
    if (!exclude.includes("claimType")) {
      setValue("claimType", null);
    }
    if (!exclude.includes("chargingType")) {
      setValue("chargingType", null);
    }
    if (!exclude.includes("lastOdometer")) {
      setValue("lastOdometer", "");
    }
    if (!exclude.includes("taxInvoice")) {
      setValue("taxInvoice", null);
    }
    setValue("transactionDate", null);
    setValue("currentOdometer", "");
    setValue("percentageChargeAtHome", "");
    setValue("chargingReport", null);
    setValue("gst", "");
    setValue("incGst", "");
    setValue("description", "");
    setValue("vendor", "");
  };

  useEffect(() => {
    if (watchDossier && watchDossier.id !== dossierId) {
      clearFieldsExcluding();
      navigate(getScopedPageUrl("createDossierExpenseClaim", watchDossier.id), {
        replace: true,
      });
    }
  }, [watchDossier]);

  useEffect(() => {
    if (watchDossier && watchDossier.id !== dossierId) {
      clearFieldsExcluding();
      setValue(
        "dossier",
        dossierOptions.find((dossier) => dossier.id === dossierId) ?? null
      );
    }
  }, [dossierId]);

  useEffect(() => {
    const chargingType =
      claimType?.id === "charging" && filteredChargingTypeOptions.length === 1
        ? filteredChargingTypeOptions[0]
        : null;

    setValue("chargingType", chargingType);

    clearFieldsExcluding(["claimType", "chargingType"]);
  }, [claimType]);

  useEffect(() => {
    if (chargingType?.id === "home-charging" && selectedDossier) {
      setValue("lastOdometer", `${selectedDossier.lastClaimedOdo || 0}`);
    }
    clearFieldsExcluding(["claimType", "lastOdometer", "chargingType"]);
  }, [chargingType]);

  useEffect(() => {
    setMinCurrentOdometer(lastOdometer ? parseInt(lastOdometer) + 1 : 0);
  }, [lastOdometer]);

  useEffect(() => {
    if (configMap.isSuccess) {
      const homeChargingRate = getEVHomeChargingRate(configMap.data);

      if (lastOdometer && currentOdometer) {
        const _percentageChargeAtHome =
          filteredChargingTypeOptions.length > 1
            ? percentageChargeAtHome
            : "100";

        const homeChargingIncGst = calculateHomeChargingIncGst(
          parseInt(lastOdometer),
          parseInt(currentOdometer),
          homeChargingRate,
          parseInt(_percentageChargeAtHome)
        );

        setValue("incGst", `${homeChargingIncGst}`);
      }
    }
  }, [lastOdometer, currentOdometer, percentageChargeAtHome]);

  useEffect(() => {
    if (
      taxInvoice &&
      (!chargingType || chargingType.id !== "home-charging") &&
      configMap.isSuccess &&
      isOCRExpenseClaimReceiptEnabled(configMap.data)
    ) {
      clearFieldsExcluding(["claimType", "chargingType", "taxInvoice"]);
      extractReceiptDataAsync({ receipt: taxInvoice });
    }
  }, [taxInvoice]);

  useEffect(() => {
    if (isSuccessExtractReceiptData && receiptData) {
      if (receiptData.vendor) {
        setValue("vendor", receiptData.vendor);
      }
      if (receiptData.description) {
        setValue("description", receiptData.description);
      }
      if (receiptData.incGst) {
        setValue("incGst", Math.abs(receiptData.incGst).toString());
      }
      if (receiptData.incGst && receiptData.exGst && displayGst) {
        setValue(
          "gst",
          toFormattedNumber(
            Math.abs(receiptData.incGst) - Math.abs(receiptData.exGst),
            2
          )
        );
      }
      if (receiptData.transactionDate) {
        setValue("transactionDate", receiptData.transactionDate);
      }
    }
  }, [isSuccessExtractReceiptData]);

  useEffect(() => {
    if (!isFetchingContact && contact && !contact.activeBankAccountSetup) {
      navigate(
        `${getScopedPageUrl("userDirectDebit")}?redirectOnSubmit=${
          location.pathname
        }`,
        { replace: true }
      );
    }
  }, [isFetchingContact]);

  const detailList = useMemo(() => {
    if (isConfirming) {
      const values = getValues();
      const detailListItems: APDetailListItem[] = [
        ...(values.claimType
          ? [
              {
                label: "Type",
                value: values.claimType?.label ?? "",
              },
            ]
          : []),
        ...(values.chargingType
          ? [
              {
                label: "Charging Type",
                value: values.chargingType?.label ?? "",
              },
            ]
          : []),
        ...(values.taxInvoice
          ? [
              {
                label:
                  values.chargingType?.id === "home-charging"
                    ? "Current Odo Image"
                    : "Tax Invoice",
                value: values.taxInvoice?.name ?? "",
              },
            ]
          : []),
        ...(values.transactionDate
          ? [
              {
                label: "Transaction Date",
                value: values.transactionDate,
              },
            ]
          : []),
        ...(values.description
          ? [
              {
                label: "Description",
                value: values.description ?? "",
              },
            ]
          : []),
        ...(values.vendor
          ? [
              {
                label: "Vendor",
                value: values.vendor ?? "",
              },
            ]
          : []),
        ...(values.lastOdometer
          ? [
              {
                label: "Last Odometer",
                value: `${toFormattedNumber(
                  parseInt(values.lastOdometer),
                  0
                )}km`,
              },
            ]
          : []),
        ...(values.currentOdometer
          ? [
              {
                label: "Current Odometer",
                value: `${toFormattedNumber(
                  parseInt(values.currentOdometer),
                  0
                )}km`,
              },
            ]
          : []),
        ...(values.percentageChargeAtHome
          ? [
              {
                label: "Percentage Charge At Home",
                value: `${values.percentageChargeAtHome}%`,
              },
            ]
          : []),
        ...(values.chargingReport
          ? [
              {
                label: "Charging Report",
                value: values.chargingReport?.name ?? "",
              },
            ]
          : []),
        ...(values.gst
          ? [
              {
                label: "GST",
                value: toDollars(parseFloat(values.gst), 2),
              },
            ]
          : []),
        ...(values.incGst
          ? [
              {
                label: "Inc GST",
                value: toDollars(parseFloat(values.incGst), 2),
              },
            ]
          : []),
      ];

      return <APDetailList items={detailListItems} />;
    }
    const items: APDetailListItem[] = [];

    return items;
  }, [isConfirming]);

  const filteredClaimTypeOptions = useMemo(() => {
    return selectedDossier
      ? filterExpenseClaimTypeOptions(
          SubmittedExpenseClaimTypeOptions,
          selectedDossier
        )
      : [];
  }, [selectedDossier]);

  const filteredChargingTypeOptions = useMemo(() => {
    return selectedDossier
      ? filterExpenseClaimChargingTypeOptions(
          ExpenseClaimChargingTypeOptions,
          selectedDossier
        )
      : [];
  }, [selectedDossier]);

  const isLoading =
    isLoadingDossierActivationCheck || isLoadingFaqs || isLoadingContact;
  const isSuccess =
    isSuccessDossierActivationCheck && isSuccessFaqs && isSuccessContact;

  return (
    <PageContainer
      title={isConfirming ? "Submit Expense Claim" : "Input Expense Claim"}
      loading={isLoading || !navigationCheckComplete}
    >
      {isSuccess && navigationCheckComplete && selectedDossier && (
        <Stack spacing={2}>
          {selectedDossier && (
            <DossierSelector
              dossiers={isConfirming ? [selectedDossier] : activeDossiers}
              selectedDossier={selectedDossier}
              redirectTo="createDossierExpenseClaim"
              showDivider
            />
          )}
          <Stack spacing={1}>
            {!isConfirming && (
              <DossierBalances
                pendingClaimsBalance={
                  selectedDossier.pendingClaimBalance.incGst ?? 0
                }
                availableBalance={
                  selectedDossier.availableBalanceExcludingClaims.incGst ?? 0
                }
                faqs={{
                  ...faqProps,
                }}
                faqAppLocationIdentifiers={{
                  pendingClaims: "ExpenseClaimsInput-PendingClaims",
                  availableBalance: "ExpenseClaimsInput-AvailForClaims",
                }}
              />
            )}
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              {faqProps.renderAppLocation(
                "ExpenseClaimsInput-WhatCanIClaim",
                <Typography
                  variant="bodySmall"
                  color="primary"
                  sx={{ textDecoration: "underline", cursor: "pointer" }}
                >
                  What can I claim?
                </Typography>,
                true
              )}
              {!isConfirming && (
                <Box>
                  <Typography variant="bodySmall">Inc GST</Typography>
                </Box>
              )}
            </Box>
          </Stack>
          <APForm
            key={dossierId}
            onSubmit={handleSubmit(onSubmit)}
            submitText={isConfirming ? "Submit" : "Next"}
            submitIcon={<PlayArrowOutlined />}
            leftButton={
              <Stack
                spacing={2}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <BackButton
                  onClick={isConfirming ? onConfirmBack : undefined}
                />

                {!isConfirming && (
                  <APButton
                    text="Budget"
                    color="primary"
                    variant="contained"
                    onClick={() => {
                      navigate(
                        getScopedPageUrl("dossierBudgets", selectedDossier.id)
                      );
                    }}
                  />
                )}

                <RestrictedAppScope scope="external">
                  <APContactHelpButton
                    regardingOpportunityId={
                      selectedDossier?.opportunity?.id ?? undefined
                    }
                  />
                </RestrictedAppScope>
              </Stack>
            }
            isLoading={isLoadingMutate || isLoadingExtractReceiptData}
            isError={Object.keys(errors).length > 0}
          >
            {isConfirming && <>{detailList}</>}

            <>
              {activeDossiers.length > 1 && (
                <APAutoComplete
                  name="dossier"
                  label="Dossier"
                  options={dossierOptions}
                  control={control}
                  validations={{ required: true }}
                  errors={errors}
                  defaultValue={toDossierOption(selectedDossier)}
                  hidden={isConfirming}
                />
              )}
              <APAutoComplete
                name="claimType"
                label="Type"
                options={filteredClaimTypeOptions}
                control={control}
                validations={{ required: true }}
                errors={errors}
                defaultValue={null}
                hidden={isConfirming}
              />
              {claimType?.id === "charging" && (
                <APAutoComplete
                  name="chargingType"
                  label="Charging Type"
                  options={filteredChargingTypeOptions}
                  control={control}
                  validations={{ required: true }}
                  errors={errors}
                  defaultValue={null}
                  hidden={
                    isConfirming || filteredChargingTypeOptions.length === 1
                  }
                />
              )}
              {!!claimType &&
                (claimType.id !== "charging" || !!chargingType) && (
                  <APFileUpload
                    name="taxInvoice"
                    label={
                      chargingType?.id === "home-charging"
                        ? "Current Odo Image"
                        : "Tax Invoice"
                    }
                    control={control}
                    errors={errors}
                    validations={{ required: true }}
                    hidden={isConfirming}
                  />
                )}
              {(taxInvoice || chargingType?.id === "home-charging") && (
                <>
                  <APDateField
                    name="transactionDate"
                    label="Transaction Date"
                    control={control}
                    defaultValue={null}
                    errors={errors}
                    setError={setError}
                    clearErrors={clearErrors}
                    minDate={
                      selectedDossier.startDate
                        ? dateFromString(selectedDossier.startDate)
                        : yearsAgo(1)
                    }
                    maxDate={endOfDay(now())}
                    minDateErrorMessage="The date must be after the start of your lease"
                    maxDateErrorMessage="The date cannot be in the future"
                    validations={{
                      required: true,
                    }}
                    hidden={isConfirming}
                    dateOnlyFormat={true}
                  />

                  {chargingType?.id !== "home-charging" && (
                    <>
                      <APTextField<ClaimInputs>
                        name="description"
                        label="Description"
                        control={control}
                        validations={{
                          required: true,
                          maxLength: 100,
                        }}
                        errors={errors}
                        defaultValue=""
                        hidden={isConfirming}
                        setValue={setValue}
                        clearable={!!receiptData?.description}
                      />
                      <APTextField<ClaimInputs>
                        name="vendor"
                        label="Vendor"
                        control={control}
                        validations={{}}
                        errors={errors}
                        defaultValue=""
                        hidden={isConfirming}
                      />
                    </>
                  )}
                  {chargingType?.id === "home-charging" && (
                    <>
                      <APTextField<ClaimInputs>
                        name="lastOdometer"
                        label="Last Odometer"
                        control={control}
                        validations={{
                          formatValidation: /[0-9]+/,
                          minValue: selectedDossier.lastClaimedOdo
                            ? selectedDossier.lastClaimedOdo
                            : 1,
                          required: true,
                        }}
                        errors={errors}
                        defaultValue={`${selectedDossier.lastClaimedOdo || 0}`}
                        endAdornment="km"
                        placeholder="xxxxx"
                        hidden={isConfirming}
                      />
                      <APTextField<ClaimInputs>
                        name="currentOdometer"
                        label="Current Odometer"
                        control={control}
                        validations={{
                          formatValidation: /[0-9]+/,
                          minValue: minCurrentOdometer,
                          required: true,
                        }}
                        errors={errors}
                        defaultValue={""}
                        endAdornment="km"
                        placeholder="xxxxx"
                        hidden={isConfirming}
                      />
                      {filteredChargingTypeOptions.length > 1 && (
                        <APTextField<ClaimInputs>
                          name="percentageChargeAtHome"
                          label="Percentage Charge At Home"
                          control={control}
                          validations={{
                            required: true,
                            formatValidation: /^(100|[1-9]?[0-9])$/,
                            customMessages: {
                              formatValidation:
                                "Value must be a number between 0 and 100",
                            },
                          }}
                          errors={errors}
                          defaultValue={""}
                          endAdornment="%"
                          placeholder="xx"
                          hidden={isConfirming}
                        />
                      )}
                      {filteredChargingTypeOptions.length > 1 && (
                        <APFileUpload
                          name="chargingReport"
                          label="Charging Report"
                          control={control}
                          errors={errors}
                          validations={{ required: true }}
                          hidden={isConfirming}
                        />
                      )}
                    </>
                  )}
                  {displayGst && (
                    <>
                      <APTextField<ClaimInputs>
                        name="gst"
                        label="GST"
                        control={control}
                        validations={{
                          required: true,
                          formatValidation: /^[0-9]+(\.[0-9]{1,2})?$/,
                          maxValue: incGst ? parseFloat(incGst) : undefined,
                          customMessages: {
                            maxValue: "Value cannot be greater than Inc GST",
                          },
                        }}
                        errors={errors}
                        defaultValue={""}
                        startAdornment="$"
                        placeholder="xxx.xx"
                        formatNumber={true}
                        hidden={isConfirming}
                      />
                    </>
                  )}
                  <APTextField<ClaimInputs>
                    name="incGst"
                    label="Inc GST"
                    control={control}
                    validations={{
                      required: true,
                      formatValidation: /^[0-9]+(\.[0-9]{1,2})?$/,
                    }}
                    errors={errors}
                    defaultValue={""}
                    startAdornment="$"
                    placeholder="xxx.xx"
                    formatNumber={true}
                    readOnly={
                      claimType?.id === "charging" &&
                      chargingType?.id === "home-charging"
                    }
                    hidden={isConfirming}
                  />
                </>
              )}
            </>

            {isConfirming && (
              <Typography
                variant="bodyLarge"
                component="span"
                sx={{
                  "& *": { display: "inline", margin: 0, padding: 0 }, // makes all nested elements inline
                }}
              >
                I declare that this expense is for this vehicle managed by
                Auto-UX and meets the{" "}
                {faqProps.renderAppLocation(
                  "ExpenseClaimsSubmit-Declaration",
                  <Typography
                    variant="bodyLarge"
                    component="span"
                    color="primary"
                    sx={{ textDecoration: "underline", cursor: "pointer" }}
                  >
                    claimable expense
                  </Typography>
                )}{" "}
                criteria.
              </Typography>
            )}
          </APForm>
        </Stack>
      )}
    </PageContainer>
  );
};

export default CreateDossierExpenseClaim;
